/**
 * useDeviceType - Unified device detection hook
 * 
 * Provides consistent breakpoint detection across the entire application.
 * Replaces fragmented useIsMobile and useMobileDetection hooks.
 * 
 * Breakpoints:
 * - Mobile: < 640px (Tailwind sm breakpoint)
 * - Tablet: 640px - 1023px
 * - Desktop: >= 1024px (Tailwind lg breakpoint)
 */

import type { DeviceType, Orientation, DeviceInfo } from './types/device-type';
export type { DeviceType, Orientation, DeviceInfo } from './types/device-type';

import { useState, useEffect, useCallback } from 'react';

// Breakpoint constants aligned with Tailwind
const MOBILE_BREAKPOINT = 640;   // sm
const TABLET_BREAKPOINT = 1024;  // lg

function getDeviceInfo(): DeviceInfo {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      deviceType: 'desktop',
      width: 1024,
      height: 768,
      orientation: 'landscape',
      isTouch: false,
    };
  }

  const width = window.innerWidth;
  const height = window.innerHeight;
  const isMobile = width < MOBILE_BREAKPOINT;
  const isTablet = width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT;
  const isDesktop = width >= TABLET_BREAKPOINT;

  let deviceType: DeviceType = 'desktop';
  if (isMobile) deviceType = 'mobile';
  else if (isTablet) deviceType = 'tablet';

  const orientation: Orientation = height > width ? 'portrait' : 'landscape';
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  return {
    isMobile,
    isTablet,
    isDesktop,
    deviceType,
    width,
    height,
    orientation,
    isTouch,
  };
}

export function useDeviceType(): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(getDeviceInfo);

  const updateDeviceInfo = useCallback(() => {
    const newInfo = getDeviceInfo();
    setDeviceInfo(prev => {
      // Only update if values actually changed to prevent unnecessary rerenders
      if (
        prev.isMobile !== newInfo.isMobile ||
        prev.isTablet !== newInfo.isTablet ||
        prev.isDesktop !== newInfo.isDesktop ||
        prev.width !== newInfo.width ||
        prev.height !== newInfo.height ||
        prev.orientation !== newInfo.orientation
      ) {
        return newInfo;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Update on mount
    updateDeviceInfo();

    // Use matchMedia for efficient breakpoint detection
    const mobileQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const tabletQuery = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT}px) and (max-width: ${TABLET_BREAKPOINT - 1}px)`);

    const handleChange = () => updateDeviceInfo();

    // Subscribe to breakpoint changes
    mobileQuery.addEventListener('change', handleChange);
    tabletQuery.addEventListener('change', handleChange);

    // Also listen for resize to track width/height/orientation changes
    window.addEventListener('resize', handleChange);
    window.addEventListener('orientationchange', handleChange);

    return () => {
      mobileQuery.removeEventListener('change', handleChange);
      tabletQuery.removeEventListener('change', handleChange);
      window.removeEventListener('resize', handleChange);
      window.removeEventListener('orientationchange', handleChange);
    };
  }, [updateDeviceInfo]);

  return deviceInfo;
}

// Convenience hooks for simpler usage
export function useIsMobileDevice(): boolean {
  return useDeviceType().isMobile;
}

export function useIsTabletDevice(): boolean {
  return useDeviceType().isTablet;
}

export function useIsDesktopDevice(): boolean {
  return useDeviceType().isDesktop;
}

export default useDeviceType;
